import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { BdExportAsFileInput } from '../export-input';
import { defer, Subject, take, throwError } from 'rxjs';
import { BdLoaderComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { BdLoaderState } from '@bindoc-projects/platform-lib/data';
import { BdCurrentUserService, UserFileService } from '@bindoc-projects/user-lib/data';
import { BdExportAsFileExportHandler } from '../export-handler';
import { saveAs } from 'file-saver';
import { BdHubspotFormService } from '../../hubspot/forms/hubspot-form.service';
import {
  BD_EXPORT_AS_FILE_DEFAULT_CONFIG,
  BdExportAsFileConfig,
} from '../export.config';
import { BdExportAsFileDialogFormHubspotContact } from './hubspot-contact/hubspot-contact';
import { BdExportAsFileDialogFormComponent } from './form/form.component';
import { BdExportAsFileDialogHubspotContactComponent } from './hubspot-contact/hubspot-contact.component';
import { BdUiInlineNotificationComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { ButtonDirective } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';

export interface BdExportAsFileDialogData {
  input: BdExportAsFileInput;
  config: BdExportAsFileConfig;
  authenticated: boolean;
}

/** Closed-with result: the export outcome the trigger component turns into a toast. */
export interface BdExportAsFileDialogResult {
  status: 'ok' | 'error';
  /** True when the export was additionally stored in the File-Center (drives the success toast copy). */
  savedInDownloadCenter?: boolean;
}

@Component({
  selector: 'bd-export-as-file-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div>
      <bd-loader [loadItem]="loading$" [initTemplate]="formTpl"></bd-loader>
    </div>

    <ng-template #formTpl>
      @if (
        config.authenticationStrategy === 'reduce' &&
        !exportHandler()?.authenticated
      ) {
        <div class="flex flex-col gap-2">
          <bd-ui-inline-notification
            variant="compact"
            severity="info"
            title="Begrenzter Export"
            [message]="reduceMessage"
          ></bd-ui-inline-notification>
          <a
            class="self-start"
            href="https://www.bindoc.de/online-meeting"
            target="_blank"
            >Kontaktieren Sie unseren Vertrieb</a
          >
        </div>
      }
      @if (
        config.authenticationStrategy === 'contact' &&
        !exportHandler()?.authenticated
      ) {
        <div class="contact-form">
          <bd-export-as-file-dialog-form-hubspot-contact
            (contactChange)="onContactChange($event)"
          >
          </bd-export-as-file-dialog-form-hubspot-contact>
        </div>
        <p-divider></p-divider>
      }

      @if (exportHandler()) {
        <div>
          <bd-export-as-file-dialog-form
            [input]="exportHandler()!.input"
            [config]="config"
            [authenticated]="exportHandler()!.authenticated"
            (inputChange)="onInputChange($event)"
          >
          </bd-export-as-file-dialog-form>
        </div>
      }

      <div class="flex flex-row gap-4 justify-end w-full">
        <button pButton severity="secondary" [text]="true" (click)="abort()">
          Abbrechen
        </button>
        <button
          pButton
          severity="primary"
          bdDialogPrimaryAction
          [disabled]="!exportHandler()?.isValid"
          data-test="export-submit"
          (click)="onExportClick()"
        >
          Exportieren
        </button>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .contact-form,
      .export-form {
        padding: 24px 0;
      }
    `,
  ],
  imports: [
    BdUiDialogPrimaryActionDirective,
    BdLoaderComponent,
    BdUiInlineNotificationComponent,
    BdExportAsFileDialogHubspotContactComponent,
    Divider,
    BdExportAsFileDialogFormComponent,
    ButtonDirective,
  ],
})
export class BdExportAsFileDialogComponent {
  private readonly _config = inject(
    DynamicDialogConfig<BdExportAsFileDialogData>,
  );
  private readonly _dialogRef = inject(
    DynamicDialogRef<BdExportAsFileDialogComponent>,
  );
  private readonly _currentUserService = inject(BdCurrentUserService);
  private readonly _hubspotFormService = inject(BdHubspotFormService);
  private readonly _fileService = inject(UserFileService, {
    optional: true,
  }) as UserFileService | null;

  public readonly loading$ = new Subject<BdLoaderState>();
  public readonly config: BdExportAsFileConfig;
  public readonly exportHandler = signal<BdExportAsFileExportHandler | null>(
    null,
  );

  constructor() {
    this._config.data.input.exportType =
      this._config.data.input.exportType ?? 'xlsx';
    this.config = this._config.data.config ?? BD_EXPORT_AS_FILE_DEFAULT_CONFIG;

    this._currentUserService.authenticated$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((authenticated) => {
        this.exportHandler.set(
          new BdExportAsFileExportHandler(
            this._hubspotFormService,
            this._config.data.input,
            this.config,
            authenticated,
            this._fileService,
          ),
        );
      });
  }

  /** Row-limit hint shown as an inline notification when the reduce strategy applies to an anonymous user. */
  public get reduceMessage(): string {
    const count = this.config.authenticationStrategyConfig?.['resultSetCount'];
    return `Der Export ist begrenzt auf ${count} Zeilen. Bitte melden Sie sich an, um den Export ohne Limit durchzuführen.`;
  }

  public abort(): void {
    this._dialogRef.close();
  }

  public onContactChange(
    contact: BdExportAsFileDialogFormHubspotContact,
  ): void {
    const handler = this.exportHandler();
    if (handler) {
      handler.contact = contact;
    }
  }

  public onInputChange(input: BdExportAsFileInput): void {
    const handler = this.exportHandler();
    if (handler) {
      handler.input = input;
    }
  }

  public onExportClick(): void {
    this.loading$.next(BdLoaderState.LOADING);
    // Defer the export() call so synchronous throws from getExport() (e.g.
    // tile responses with warning status) surface as error notifications
    // instead of leaving the dialog stuck on the loading spinner.
    defer(() => {
      const handler = this.exportHandler();
      if (!handler) {
        return throwError(() => new Error('Export handler not ready'));
      }
      return handler.export();
    }).subscribe({
      next: (exportResult) => {
        const handler = this.exportHandler();
        if (exportResult.errors || !handler) {
          console.error(exportResult.errors);
          this._dialogRef.close(<BdExportAsFileDialogResult>{
            status: 'error',
          });
          return;
        }
        saveAs(
          exportResult.result,
          `${handler.input.filename}.${handler.input.exportType}`,
        );
        this._dialogRef.close(<BdExportAsFileDialogResult>{
          status: 'ok',
          savedInDownloadCenter: !!handler.input.saveInDownloadCenter,
        });
      },
      error: (err) => {
        console.error(err);
        this._dialogRef.close(<BdExportAsFileDialogResult>{ status: 'error' });
      },
    });
  }
}
